/**
 * Tracks project listing IDs with an in-flight stage mutation. Uses the shared
 * `jotaiStore` (`<Provider store={jotaiStore}>` in App.tsx). From outside React,
 * use `getAtomValue` / `setAtomValue` from `@/shared/jotai` so reads/writes stay
 * on the same store as `useAtomValue` subscribers.
 */

import { atom } from 'jotai';
import type { Atom } from 'jotai';
import { selectAtom } from 'jotai/utils';

import { getAtomValue, setAtomValue } from '@/shared/jotai';

export const pendingProjectListingStageIdsAtom = atom<ReadonlySet<string>>(
  new Set<string>(),
);

const isPendingProjectListingStageAtomCache = new Map<string, Atom<boolean>>();

export const isPendingProjectListingStageAtom = (
  listingId: string,
): Atom<boolean> => {
  const cached = isPendingProjectListingStageAtomCache.get(listingId);
  if (cached) {
    return cached;
  }

  const derived = selectAtom(pendingProjectListingStageIdsAtom, ids =>
    ids.has(listingId),
  );
  isPendingProjectListingStageAtomCache.set(listingId, derived);
  return derived;
};

export const addPendingProjectListingStage = (listingId: string): void => {
  const prev = getAtomValue(pendingProjectListingStageIdsAtom);
  if (prev.has(listingId)) {
    return;
  }

  const next = new Set(prev);
  next.add(listingId);
  setAtomValue(pendingProjectListingStageIdsAtom, next);
};

export const removePendingProjectListingStage = (listingId: string): void => {
  const prev = getAtomValue(pendingProjectListingStageIdsAtom);
  if (!prev.has(listingId)) {
    return;
  }

  const next = new Set(prev);
  next.delete(listingId);
  setAtomValue(pendingProjectListingStageIdsAtom, next);
};

export const isPendingProjectListingStage = (listingId: string): boolean =>
  getAtomValue(pendingProjectListingStageIdsAtom).has(listingId);
